import {Combo} from '@mintdata-wsp/common/components/combo/Combo';
import {MDIcon, MDIconSize} from '@mintdata-wsp/common/components/icon/MDIcon';
import {MDIcons} from '@mintdata-wsp/common/components/icon/MDIcons';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {Flow} from '@mintdata-wsp/common/state/ui/visualDesigner/FlowState';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {Row} from '@mintdata-wsp/wsp/ui/components/forms/Row';
import {ButtonSize, ButtonStyle, WSPButton} from '@mintdata-wsp/wsp/ui/components/general/WSPButton';
import {DotsMenu} from '@mintdata-wsp/wsp/ui/components/menus/DotsMenu';
import {MenuItem} from '@mintdata-wsp/wsp/ui/components/menus/MenuItem';
import {BaseComponentWithContext} from '@mintdata/mint-kit/api/components/BaseComponentWithContext';
import {IListItem, IListItemState, ListModel} from '@mintdata/mint-kit/api/models/List';
import {IStyles} from '@mintdata/mint-kit/impl/util/Styles';
import * as classnames from 'classnames/bind';
import {observer} from 'mobx-react';
import * as React from 'react';

const styles = require('./FlowSwitcher.less');
const cx = classnames.bind(styles);

@observer
export class FlowSwitcher extends BaseComponentWithContext<any, any>
{
    _handleFlowSelection = (item:any) =>
    {
        const flow = item.data as Flow;
        const {projectId, designId} = flow;

        controllers.navigation.toFlowEditor(projectId, designId, flow.id);
    };

    _comboCaptionRenderer = (data:any, state:any, styles:IStyles):JSX.Element =>
    {
        const {sampleDesign} = rootState.ui.visualDesigner;

        const {label} = data;
        const {isOpened} = state;

        return (
            <div className={cx('caption')}>
                <span>{label}</span>
                {
                    !sampleDesign &&
                    <MDIcon className={cx({reverseIcon: isOpened}, 'icon')}
                            name={MDIcons.common.ArrowDropdown}
                            size={MDIconSize.large}
                    />
                }
            </div>
        );
    };

    private _computeMenu = (flow:Flow):MenuItem[] =>
    {
        const renameFlowItem = MenuItem.simple('renameFlow', 'Rename', () =>
        {
            controllers.ui.flow.renameFlowDialog(flow);
        });

        const duplicateFlowItem = MenuItem.simple('duplicateFlow', 'Duplicate', () =>
        {
            controllers.ui.flow.duplicateFlowAndNavigate(flow);
        });

        const deleteFlowItem = MenuItem.simple('deleteFlow', 'Delete', () =>
        {
            controllers.ui.flow.deleteFlowDialog(flow);
        });

        return [renameFlowItem, duplicateFlowItem, deleteFlowItem];
    };

    _comboItemsRenderer = (data:any, state:IListItemState, styles:IStyles):JSX.Element =>
    {
        const {sampleDesign} = rootState.ui.visualDesigner;

        if( sampleDesign ) return null;

        const flow:Flow = data.data;
        const isSelected = state.isSelected;

        return (
            <div className={cx('functionCard', {isSelected})}>
                <MDIcon className={cx('icon')}
                        name={MDIcons.common.CloudFunction}
                        size={MDIconSize.xlarge}
                />

                <div className={cx('infoHolder')}>
                    <div className={cx('title')}>{flow.name}</div>
                    <div className={cx('subInfoHolder')}>
                        {flow.orderedInputNames.map((name) => <span key={name}>{name}</span>)}
                    </div>
                </div>

                <DotsMenu menuItems={this._computeMenu(flow)}
                          className={cx('dotsMenu')}
                />
            </div>
        );
    };

    private _handleAddFlowClick = () =>
    {
        const {flow} = controllers.ui;
        const {projectId, designId} = flow.getOpenFlow();

        const {popupManager} = this.context;
        popupManager.onNonPopupItemClicked();

        flow.createFlowAndNavigate(projectId, designId);
    };

    private _selectCurrentItem(listModel:ListModel<any>):void
    {
        const {flow} = controllers.ui;
        const openFlow = flow.getOpenFlow();

        listModel.getItems().map((item:IListItem<{label:string, data:Flow}>) =>
        {
            const flow:Flow = item.getData().data;

            if( flow.id === openFlow.id )
            {
                item.select();
            }
        });
    }

    private _createListModel():ListModel<any>
    {
        const {flow} = controllers.ui;

        const flows = flow.getFlows();
        const items = flows.map((flow:Flow) => ({label: flow.name, data: flow}));

        return new ListModel(items);
    }

    render()
    {
        const {sampleDesign} = rootState.ui.visualDesigner;

        const listModel = this._createListModel();
        this._selectCurrentItem(listModel);

        return (
            <div className={cx('flowSwitcher', {isSampleDesign: !!sampleDesign})}>
                <Combo
                    dataSource={listModel}
                    onSelectItem={this._handleFlowSelection}
                    contentRenderer={this._comboCaptionRenderer}
                    itemRenderer={this._comboItemsRenderer}
                >
                    {
                        !sampleDesign &&
                        <header>
                            <Row className={cx('header')}>
                                Flows
                            </Row>
                        </header>
                    }
                    {
                        !sampleDesign &&
                        <footer>
                            <Row className={cx('footer')}>
                                <WSPButton
                                    caption={'Add Flow'}
                                    className={cx('addFlowBtn')}
                                    style={ButtonStyle.Simple}
                                    size={ButtonSize.Small}
                                    onClick={this._handleAddFlowClick} />
                            </Row>
                        </footer>
                    }
                </Combo>
            </div>
        );
    }
}
